import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Link2,
  Package,
  Search,
  Unlink,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useTelnyxSimCards } from '../hooks/useSimCards';
import type { ManagedSimCard, SimProvider } from '../types';
import { SimInventoryHeader } from './SimInventoryHeader';
import { SimSummaryCard } from './SimSummaryCard';
import {
  TelnyxInventoryTable,
  type TelnyxSortKey,
} from './TelnyxInventoryTable';

interface TelnyxInventoryProps {
  onProviderChange: (provider: SimProvider) => void;
}

const PAGE_LENGTH = 25;

const TELNYX_STATUSES = [
  'enabled',
  'disabled',
  'standby',
  'registered',
  'registering',
  'enabling',
  'disabling',
  'setting_standby',
  'data_limit_exceeded',
  'unauthorized_imei',
  'blocked',
  'abolished',
] as const;

function assignmentLabel(sim: ManagedSimCard): string {
  const device = sim.gateway ?? sim.transmitter;
  return device?.name || device?.customid || device?.dev_id || '';
}

function sortValue(sim: ManagedSimCard, sortKey: TelnyxSortKey): string | number {
  if (sortKey === 'assignment') return assignmentLabel(sim).toLocaleLowerCase();
  if (sortKey === 'usage') return sim.usage?.this_cycle ?? 0;
  if (sortKey === 'last_checked') return sim.last_checked ? new Date(sim.last_checked).getTime() : 0;
  return sim[sortKey].toLocaleLowerCase();
}

export function TelnyxInventory({ onProviderChange }: TelnyxInventoryProps) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<TelnyxSortKey>('status');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const query = useTelnyxSimCards();
  const simCards = query.data ?? [];

  const filteredSimCards = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return simCards.filter((sim) => {
      if (status !== 'all' && sim.status !== status) return false;
      if (!term) return true;
      return [
        sim.iccid,
        sim.name,
        assignmentLabel(sim),
        sim.gateway?.dev_id,
        sim.transmitter?.dev_id,
      ].some((value) => value?.toLocaleLowerCase().includes(term));
    });
  }, [search, simCards, status]);

  const sortedSimCards = useMemo(() => (
    [...filteredSimCards].sort((left, right) => {
      const leftValue = sortValue(left, sortKey);
      const rightValue = sortValue(right, sortKey);
      if (leftValue < rightValue) return -1 * sortDirection;
      if (leftValue > rightValue) return sortDirection;
      return 0;
    })
  ), [filteredSimCards, sortDirection, sortKey]);

  useEffect(() => {
    setPage(0);
  }, [search, status, sortDirection, sortKey]);

  const totalPages = Math.ceil(sortedSimCards.length / PAGE_LENGTH);
  const pageItems = sortedSimCards.slice(page * PAGE_LENGTH, (page + 1) * PAGE_LENGTH);
  const enabled = simCards.filter((sim) => sim.status === 'enabled').length;
  const disabled = simCards.filter((sim) => sim.status === 'disabled').length;
  const standby = simCards.filter((sim) => sim.status === 'standby').length;
  const linked = simCards.filter((sim) => sim.gateway || sim.transmitter).length;
  const attention = simCards.length - enabled - disabled - standby;

  const handleSort = (key: TelnyxSortKey) => {
    if (key === sortKey) {
      setSortDirection((direction) => (direction === 1 ? -1 : 1));
      return;
    }
    setSortKey(key);
    setSortDirection(1);
  };

  return (
    <div className="space-y-5">
      <SimInventoryHeader
        provider="telnyx"
        onProviderChange={onProviderChange}
        onRefresh={() => query.refetch()}
        isRefreshing={query.isFetching}
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <SimSummaryCard
          title="Total SIMs"
          value={simCards.length}
          icon={<Package className="h-4 w-4 text-muted-foreground" />}
          isLoading={query.isLoading}
        />
        <SimSummaryCard
          title="Enabled"
          value={enabled}
          icon={<Wifi className="h-4 w-4 text-green-500" />}
          isLoading={query.isLoading}
          valueClassName="text-green-600"
        />
        <SimSummaryCard
          title="Disabled"
          value={disabled}
          icon={<WifiOff className="h-4 w-4 text-gray-400" />}
          isLoading={query.isLoading}
        />
        <SimSummaryCard
          title="Standby"
          value={standby}
          icon={<WifiOff className="h-4 w-4 text-amber-500" />}
          isLoading={query.isLoading}
          valueClassName="text-amber-600"
        />
        <SimSummaryCard
          title="Linked"
          value={linked}
          icon={<Link2 className="h-4 w-4 text-blue-500" />}
          isLoading={query.isLoading}
          valueClassName="text-blue-600"
        />
        <SimSummaryCard
          title="Needs Attention"
          value={attention}
          icon={<AlertTriangle className="h-4 w-4 text-orange-500" />}
          isLoading={query.isLoading}
          valueClassName={attention ? 'text-orange-600' : undefined}
        />
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative min-w-[240px] max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search ICCID, transmitter, gateway..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[190px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {TELNYX_STATUSES.map((value) => (
              <SelectItem key={value} value={value}>
                {value.replace(/_/g, ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="ml-auto whitespace-nowrap text-sm text-muted-foreground">
          {sortedSimCards.length} SIM{sortedSimCards.length !== 1 ? 's' : ''} found
        </span>
      </div>

      {query.isLoading ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            {[...Array(5)].map((_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : query.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">Error loading Telnyx SIM cards. Please try again.</p>
            <Button variant="outline" className="mt-4" onClick={() => query.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : pageItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {search || status !== 'all'
                ? 'No Telnyx SIM cards match your filters.'
                : 'No Telnyx SIM cards have synced yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <TelnyxInventoryTable
            simCards={pageItems}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
          />

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((value) => Math.max(0, value - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((value) => Math.min(totalPages - 1, value + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {simCards.length > 0 && linked < simCards.length && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Unlink className="h-3.5 w-3.5" />
          {simCards.length - linked} SIM{simCards.length - linked !== 1 ? 's are' : ' is'} not linked to a transmitter or gateway.
        </p>
      )}
    </div>
  );
}
